import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceWithdrawalCard as Card } from '@shared/api/contracts/compliance';
import type { ReasonFactorValue } from '@shared/ui';
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  StatusBanner,
  Skeleton,
  SkeletonStatus,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TypographySmall,
} from '@shared/ui';
import { ComplianceDecisionDialog, type WithdrawalDecisionKind } from '@features/compliance-decision';
import { ComplianceThread } from '@features/compliance-thread';
import {
  ComplianceAddressBlock,
  ComplianceCardHeader,
  ComplianceChecks,
  ComplianceDecisionList,
  ComplianceReports,
} from './compliance-sections';

const ACTION_ORDER: WithdrawalDecisionKind[] = ['APPROVE', 'REQUEST_INFORMATION', 'RETURN', 'REJECT'];
const ACTION_VARIANT = {
  APPROVE: 'default',
  REQUEST_INFORMATION: 'secondary',
  RETURN: 'outline',
  REJECT: 'destructive',
} as const;

interface ComplianceCardProps {
  open: boolean;
  card: Card | null;
  loading?: boolean;
  reviewer: boolean;
  actionLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onDecide: (decision: WithdrawalDecisionKind, value: ReasonFactorValue) => Promise<boolean>;
  onReply: (text: string) => Promise<void>;
}

export function ComplianceCard({
  open,
  card,
  loading,
  reviewer,
  actionLoading,
  onOpenChange,
  onDecide,
  onReply,
}: ComplianceCardProps) {
  const { t } = useTranslation('cabinet');
  const [decision, setDecision] = useState<WithdrawalDecisionKind | null>(null);
  const allowed = new Set(card?.allowedDecisions ?? []);
  const waiting = card?.decisions.some((item) => item.state === 'PENDING_SECOND') ?? false;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent>
          <SheetBody>
            <SheetHeader>
              <SheetTitle>{card ? `#${card.withdrawal.id}` : t('compliance.cardTitle')}</SheetTitle>
            </SheetHeader>
            {loading || !card ? (
              <SkeletonStatus className="mt-6 space-y-3" label={t('workspace.loading')}>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-16 w-full" />
              </SkeletonStatus>
            ) : (
              <div className="mt-4 space-y-6">
                <ComplianceCardHeader card={card} />
                {waiting ? <StatusBanner>{t('compliance.pendingSecondBanner')}</StatusBanner> : null}
                {card.secondApproverRequired ? <StatusBanner>{t('compliance.secondRequiredHint')}</StatusBanner> : null}
                <section className="space-y-2">
                  <TypographySmall>{t('compliance.sections.checks')}</TypographySmall>
                  <ComplianceChecks checks={card.checks} />
                </section>
                <section className="space-y-2">
                  <TypographySmall>{t('compliance.sections.reports')}</TypographySmall>
                  <ComplianceReports card={card} />
                </section>
                <section className="space-y-2">
                  <TypographySmall>{t('compliance.sections.address')}</TypographySmall>
                  <ComplianceAddressBlock card={card} />
                </section>
                <section className="space-y-2">
                  <TypographySmall>{t('compliance.sections.decisions')}</TypographySmall>
                  <ComplianceDecisionList card={card} />
                </section>
                <section className="space-y-2">
                  <TypographySmall>{t('compliance.sections.thread')}</TypographySmall>
                  <ComplianceThread
                    messages={card.messages}
                    canReply={card.withdrawal.status === 'info_requested' && reviewer}
                    reply={onReply}
                  />
                </section>
              </div>
            )}
          </SheetBody>
          {card ? (
            <div className="flex shrink-0 flex-col gap-2 border-t border-border bg-card p-4 sm:flex-row sm:flex-wrap">
              <TooltipProvider delayDuration={200}>
                {ACTION_ORDER.filter((item) => allowed.has(item)).map((item) => (
                  <Tooltip key={item}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex w-full sm:w-auto">
                        <Button
                          type="button"
                          variant={ACTION_VARIANT[item]}
                          className="w-full sm:w-auto"
                          disabled={!reviewer || actionLoading}
                          onClick={() => setDecision(item)}
                        >
                          {t(`compliance.decisions.${item}.confirm`)}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!reviewer ? <TooltipContent>{t('compliance.reviewerRequired')}</TooltipContent> : null}
                  </Tooltip>
                ))}
              </TooltipProvider>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
      <ComplianceDecisionDialog
        open={Boolean(decision)}
        decision={decision}
        loading={actionLoading}
        onOpenChange={(next) => {
          if (!next) setDecision(null);
        }}
        onConfirm={(value) => {
          if (!decision) return;
          void onDecide(decision, value).then((ok) => {
            if (ok) setDecision(null);
          });
        }}
      />
    </>
  );
}
